import type { AbstractFeature, AbstractFeatures, Feature, FeatureComputer, InferFeatureName, InferFeatureReturnType, InferParentComputeType } from "@protontech/autofill/types";
import type { Fnode } from "@protontech/fathom";

import { utils } from "@protontech/fathom";

const { linearScale } = utils;

/** Factory for Features: auto-infers N (name), R (return value), and P (parents)
 * without explicit type annotations. Pass `{ private: true }` to keep the feature
 * in the group's output type but exclude it from `FeatureComputer.features` and
 * therefore from trained-model rules (eg: pre-computed form feature only used in fields). */
export const feature = <N extends string, R, P extends AbstractFeatures>(name: N, parents: P, compute: (parents: InferParentComputeType<P>, fnode: Fnode) => R, opts?: { private?: true }): Feature<N, R, P> => ({
  name,
  parents,
  compute,
  ...(opts ?? {}),
});

/** Derives a count feature from any array-returning feature (eg: a list of HTMLElements). */
export const featureCount = <N extends string, R extends unknown[], P extends AbstractFeatures>(feat: Feature<N, R, P>) => feature(`${feat.name}Count`, { [feat.name]: feat }, (parents) => (parents[feat.name] as R).length);

/** Derives a linearly scaled [0, 1] feature from any numeric feature. */
export const featureScaled = <N extends string, P extends AbstractFeatures>(feat: Feature<N, number, P>, min: number, max: number): Feature<`${N}Scaled`, number, Record<N, Feature<N, number, P>>> =>
  feature(`${feat.name}Scaled`, { [feat.name]: feat } as Record<N, Feature<N, number, P>>, (parents) => linearScale(parents[feat.name] as number, min, max));

/** Combines two numerical features that already share the same scale */
export const featuresInnerProduct = <F1 extends AbstractFeature, F2 extends AbstractFeature, P extends Record<InferFeatureName<F1>, F1> & Record<InferFeatureName<F2>, F2>>(
  feat1: F1,
  feat2: F2,
): Feature<`${InferFeatureName<F1>},${InferFeatureName<F2>}`, number, P> =>
  feature(
    `${feat1.name},${feat2.name}` as `${InferFeatureName<F1>},${InferFeatureName<F2>}`,
    {
      [feat1.name]: feat1,
      [feat2.name]: feat2,
    } as P,
    (parents) => Math.sqrt(Number(parents[feat1.name as keyof InferParentComputeType<P>]) * Number(parents[feat2.name as keyof InferParentComputeType<P>])),
  );

export const featuresProduct = <F1 extends AbstractFeature, F2 extends AbstractFeature, P extends Record<InferFeatureName<F1>, F1> & Record<InferFeatureName<F2>, F2>>(
  feat1: F1,
  feat2: F2,
): Feature<`${InferFeatureName<F1>},${InferFeatureName<F2>}`, number, P> =>
  feature(
    `${feat1.name},${feat2.name}` as `${InferFeatureName<F1>},${InferFeatureName<F2>}`,
    {
      [feat1.name]: feat1,
      [feat2.name]: feat2,
    } as P,
    (parents) => Number(parents[feat1.name as keyof InferParentComputeType<P>]) * Number(parents[feat2.name as keyof InferParentComputeType<P>]),
  );

/** Strips the parent type parameter `P` from a feature, retaining only name and return
 * type. Prevents recursive type expansion at export boundaries. Internal use only. */
type FlatFeature<F extends AbstractFeature> = Feature<InferFeatureName<F>, InferFeatureReturnType<F>, AbstractFeatures>;
type FlatFeatures<F extends AbstractFeatures> = { [K in keyof F]: FlatFeature<F[K]> };

/** Casts a features map to its flat equivalent, erasing parent type chains to prevent recursive
 * DAG expansion at module boundaries. compute() return types remain accurate. */
export const flattenFeatures = <F extends AbstractFeatures>(features: F) => features as unknown as FlatFeatures<F>;

/** Builds a runnable `FeatureComputer` from three pre-baked arrays produced by
 * the `*.sorted.gen.ts` codegen.
 *
 * - `sorted`.  : the full DAG in topological order.
 * - `features` : non-private output names. Drives the rules layer
 * - `private`  : private output names. Kept in the returned object
 *
 * `compute()` keeps the full DAG state in a local `parents` record (so each
 * child's `parents[name]` lookup works for both transient and output parents),
 * then copies the public & private feats, into a fresh `result`. */
export const getComputerForFeatures = <F extends AbstractFeatures>({ sorted, features, private: privateFeatures }: { sorted: readonly AbstractFeature[]; features: readonly string[]; private: readonly string[] }) =>
  ({
    compute: (fnode: Fnode) => {
      const parents: Record<string, unknown> = {};
      const result: Record<string, unknown> = {};

      for (const feature of sorted) parents[feature.name] = feature.compute(parents, fnode);
      for (const name of features) result[name] = parents[name];
      for (const name of privateFeatures) result[name] = parents[name];

      return result;
    },
    features,
  }) as FeatureComputer<FlatFeatures<F>>;

/** Gen-time guard on a computer's pre-baked arrays (codegen-only, never runtime): unique
 * names, each parent before its child, every output present in `sorted`. A malformed DAG
 * fails the build rather than miscomputing silently past the `as FeatureComputer<F>` cast. */
export const validateComputerInputs = ({ sorted, features, private: privateFeatures }: { sorted: readonly AbstractFeature[]; features: readonly string[]; private: readonly string[] }) => {
  const computed = new Set<string>();
  for (const f of sorted) {
    if (computed.has(f.name)) throw new Error(`duplicate feature '${f.name}' in sorted DAG`);
    for (const p of Object.values(f.parents)) {
      if (!computed.has(p.name)) throw new Error(`feature '${f.name}' precedes its parent '${p.name}' in sorted DAG`);
    }
    computed.add(f.name);
  }

  for (const name of [...features, ...privateFeatures]) {
    if (!computed.has(name)) throw new Error(`output '${name}' missing from sorted DAG`);
  }
};

/** Feature-specific topological sort using Kahn's algorithm. */
export const topologicalSort = (features: AbstractFeatures) => {
  // create a mutable collection of edges since we can't modify the input
  const parentToChildren = new Map<AbstractFeature, Set<AbstractFeature>>();
  const childToParents = new Map<AbstractFeature, Set<AbstractFeature>>();

  const to_remove = new Array<AbstractFeature>();

  // traverse the graph and collect all the nodes
  const to_visit = Array.from(Object.values(features));
  const seen = new Set(to_visit);

  while (to_visit.length > 0) {
    const node = to_visit.pop()!; // can't be undefined because of the loop condition
    // start by processing the leaf nodes
    if (Object.keys(node.parents).length === 0) {
      to_remove.push(node);
    } else {
      // if it's not a leaf node, put it in the pool of all possible nodes
      Object.values(node.parents).forEach((parent) => {
        // Mark parent as seen when queuing, otherwise a node reachable as a
        // parent from multiple children gets pushed (and later processed)
        // multiple times, producing duplicate entries in the sorted output.
        if (!seen.has(parent)) {
          to_visit.push(parent);
          seen.add(parent);
        }
        childToParents.get(node)?.add(parent) ?? childToParents.set(node, new Set([parent])); // eslint-disable-line @typescript-eslint/no-unused-expressions
        parentToChildren.get(parent)?.add(node) ?? parentToChildren.set(parent, new Set([node])); // eslint-disable-line @typescript-eslint/no-unused-expressions
      });
    }
    seen.add(node);
  }

  // start the processing
  const sorted = new Array<AbstractFeature>();

  // continue until we're done
  while (to_remove.length > 0) {
    const node = to_remove.pop()!; // can't be undefined because of the loop condition
    // remove all the edges that go from this node
    parentToChildren.get(node)?.forEach((child) => {
      const parents = childToParents.get(child);
      parents?.delete(node);
      // check to see if removing this edge created a leaf node so we can process it
      if (parents?.size === 0) {
        to_remove.unshift(child);
        childToParents.delete(child);
      }
    });
    sorted.push(node);
  }

  // this can happen if there are loops
  if (childToParents.size > 0) {
    // just dump out the entire graph to make it easier to troubleshoot
    throw new Error(
      `loop detected with elements:\n  ${Array.from(childToParents.entries())
        .map(
          ([child, parents]) =>
            `${child.name} -> {${Array.from(parents)
              .map((parent) => parent.name)
              .sort()
              .join(", ")}}`,
        )
        .sort()
        .join("\n  ")}`,
    );
  }

  return sorted;
};

export const computeFeatures = <F extends AbstractFeatures>(featureComputer: FeatureComputer<F>, fnode: Fnode) => featureComputer.compute(fnode);
